import React, { useEffect, useRef, useState } from 'react';
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { ReportFileTypeEnum } from '../../../enums/ReportFileEnum';
import { ReportFileType } from '../../delivery/download/graphql/queries/getReportFileListing';
import { useDeliveryReportFileQuery, ReportFileFiltersType } from '../../delivery/download/hooks/useDeliveryReportFileQuery';
import { useReportOperators } from '../../delivery/download/hooks/useReportOperators';
import WmsReportFileFilter from '../../wms/download/components/WmsReportFileFilter';
import WmsReportFileColumns from '../../wms/download/components/WmsReportFileColumns';
import {
    GET_BATCH_REPORT_DOWNLOAD_URLS,
    GetBatchReportDownloadUrlsResponse,
    GetBatchReportDownloadUrlsVariables,
} from '../../delivery/download/graphql/queries/getBatchReportFileUrls';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { getLastWeekDateRange } from '../../../utils/dateTimeUtils';
import { resolveDownloadType } from './downloadType';

/**
 * 合作商管控下载记录页，默认类型取 URL `?type=`（风控日志导出跳转时带 51），缺省为「申诉审核导出」。
 */
const PartnerControlDownloadPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const defaultType = resolveDownloadType(searchParams.get('type'));
    const initialFilters: ReportFileFiltersType = {
        type: defaultType,
        name: undefined,
        reportDate: undefined,
        operatorId: undefined,
    };
    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(initialFilters);
    const [filters, setFilters] = useState<ReportFileFiltersType>(initialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    const hasMergedDefaultFiltersRef = useRef(false);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const { operatorOptions, loading: operatorsLoading, defaultFilters } = useReportOperators();
    const { reportFileData, loading: reportLoading, refetch, totalElements, buildQueryVariables } =
        useDeliveryReportFileQuery(
            {
                ...filters,
                reportDate: filters.reportDate ?? (getLastWeekDateRange() as [string | null, string | null]),
            },
            currentPage,
            pageSize,
            !defaultFilters,
        );

    const [getBatchDownloadUrls] = useLazyQuery<GetBatchReportDownloadUrlsResponse, GetBatchReportDownloadUrlsVariables>(
        GET_BATCH_REPORT_DOWNLOAD_URLS,
    );

    useEffect(() => {
        if (!defaultFilters || hasMergedDefaultFiltersRef.current) return;
        const merged = { ...defaultFilters, type: defaultType, reportDate: undefined };
        setTempFilters(merged);
        setFilters(merged);
        hasMergedDefaultFiltersRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilters]);

    useEffect(() => {
        refetch(buildQueryVariables());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, currentPage, pageSize]);

    const handleSelectChange = (keys: React.Key[], rows: ReportFileType[]) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const reset = { ...(defaultFilters ?? initialFilters), type: defaultType };
        setTempFilters(reset);
        setFilters(reset);
        setCurrentPage(1);
    };

    const generateFilename = (index: number, reportName?: string) => {
        const ts = new Date().toISOString().split('T')[0];
        if (reportName) return /\.xlsx$/i.test(reportName) ? reportName : `${reportName}.xlsx`;
        return `Report_${index + 1}_${ts}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning(t('delivery.download.messages.selectReport'));
            return;
        }
        setDownloadLoading(true);
        try {
            const reportIds = selectedRows.map((r) => parseInt(r.id.toString(), 10));
            const { data } = await getBatchDownloadUrls({ variables: { reportIds }, fetchPolicy: 'network-only' });
            const downloadUrls = data?.getBatchReportDownloadUrls;
            if (!downloadUrls || downloadUrls.length === 0) {
                message.error(t('delivery.download.messages.downloadFailed'));
                return;
            }
            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                if (!url) continue;
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                await downloadFile(url, generateFilename(i, reportName));
                if (i < downloadUrls.length - 1) await new Promise((r) => setTimeout(r, 500));
            }
            message.success(t('delivery.download.messages.downloadSuccess'));
        } catch {
            message.error(t('delivery.download.messages.downloadFailed'));
        } finally {
            setDownloadLoading(false);
        }
    };

    const reportTypeOptions = [
        { value: ReportFileTypeEnum.APPEAL_REVIEW_EXPORT.toString(), label: t('enum.reportTypeOptions.appealReviewExport') },
        { value: ReportFileTypeEnum.DRIVER_ACTIVITY_RISK_LOG_EXPORT.toString(), label: t('enum.reportTypeOptions.driverActivityRiskLogExport') },
    ];

    return (
        <div>
            <h1 className="text-2xl font-bold mb-4">{t('partnerControl.download.title')}</h1>

            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                <WmsReportFileFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    selectedRows={selectedRows}
                    operatorOptions={operatorOptions}
                    reportTypeOptions={reportTypeOptions}
                />

                <div className="flex justify-end items-center gap-2 mb-3">
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleBatchDownload}
                        loading={downloadLoading}
                        disabled={selectedRows.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.download')} ({selectedRows.length})</span>
                    </Button>
                    <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                        <span className="font-bold">{t('wms.download.filters.filter')}</span>
                    </Button>
                    <Button onClick={handleResetFilters} className="w-auto min-w-24">
                        <span className="font-bold">{t('wms.download.filters.reset')}</span>
                    </Button>
                </div>

                <WmsReportFileColumns
                    reportFileData={reportFileData}
                    loading={reportLoading}
                    selectedRowKeys={selectedRowKeys}
                    onSelectChange={handleSelectChange}
                />
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
                />
            </div>
        </div>
    );
};

export default PartnerControlDownloadPage;
